import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  Code2, 
  Database, 
  ShieldCheck, 
  Cpu, 
  Globe, 
  Layers, 
  Terminal, 
  Layout, 
  BarChart3, 
  Dna, 
  Settings2,
  Award,
  Clock,
  FileQuestion,
  ChevronRight,
  PenTool,
  Monitor
} from 'lucide-react';

const Skillcourse = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  // Course Data from all images (Image 3 to Image 8)
  const courses = [
    { id: 1, title: 'Web Development', category: 'DEVELOPMENT', level: 'BEGINNER', icon: Globe, desc: 'Build and test your skills in HTML, CSS, JavaScript, and modern web frameworks.' },
    { id: 2, title: 'Python Programming', category: 'PROGRAMMING', level: 'INTERMEDIATE', icon: Terminal, desc: 'Evaluate your knowledge of Python fundamentals, logic building, and problem-solving.' },
    { id: 3, title: 'Java Programming', category: 'PROGRAMMING', level: 'ADVANCED', icon: Code2, desc: 'Assess your understanding of Java concepts including OOP, syntax, and application logic.' },
    { id: 4, title: 'C Programming', category: 'PROGRAMMING', level: 'INTERMEDIATE', icon: Terminal, desc: 'Test your core programming skills including memory management and logic building in C.' },
    { id: 5, title: 'C++ Programming', category: 'PROGRAMMING', level: 'ADVANCED', icon: Code2, desc: 'Validate your knowledge of C++ including OOP, STL, and performance-based coding.' },
    { id: 6, title: 'Ruby Programming', category: 'PROGRAMMING', level: 'INTERMEDIATE', icon: Terminal, desc: 'Measure your skills in Ruby syntax, scripting, and backend development basics.' },
    { id: 7, title: 'R Programming', category: 'DATA SCIENCE', level: 'BEGINNER', icon: BarChart3, desc: 'Test your ability in statistical computing, data analysis, and visualization using R.' },
    { id: 8, title: 'Data Science', category: 'DATA SCIENCE', level: 'ADVANCED', icon: BarChart3, desc: 'Evaluate your skills in data analysis, statistics, and data-driven decision making.' },
    { id: 9, title: 'Machine Learning', category: 'AI', level: 'ADVANCED', icon: Cpu, desc: 'Assess your understanding of ML algorithms, models, and real-world applications.' },
    { id: 10, title: 'Cloud Security', category: 'SECURITY', level: 'INTERMEDIATE', icon: ShieldCheck, desc: 'Test your knowledge of securing cloud infrastructure and data protection techniques.' },
    { id: 11, title: 'Hardware & Networking', category: 'IT', level: 'BEGINNER', icon: Settings2, desc: 'Validate your fundamentals in computer hardware, networking, and system architecture.' },
    { id: 12, title: 'Network Security', category: 'SECURITY', level: 'ADVANCED', icon: ShieldCheck, desc: 'Assess your skills in protecting networks, encryption, and cybersecurity concepts.' },
    { id: 13, title: 'Generative AI', category: 'AI', level: 'INTERMEDIATE', icon: Cpu, desc: 'Test your understanding of AI models like GPT, LLMs, and content generation systems.' },
    { id: 14, title: 'Blockchain Core', category: 'DEVELOPMENT', level: 'ADVANCED', icon: Layers, desc: 'Evaluate your knowledge of blockchain technology, smart contracts, and decentralization.' },
    { id: 15, title: 'DevOps', category: 'IT', level: 'INTERMEDIATE', icon: Terminal, desc: 'Assess your skills in CI/CD, automation, deployment, and system reliability practices.' },
    { id: 16, title: 'Microsoft Word', category: 'PRODUCTIVITY', level: 'BEGINNER', icon: Layout, desc: 'Test your proficiency in document creation, formatting, and productivity features.' },
    { id: 17, title: 'Microsoft Excel', category: 'PRODUCTIVITY', level: 'BEGINNER', icon: BarChart3, desc: 'Evaluate your skills in formulas, data analysis, and spreadsheet management.' },
    { id: 18, title: 'Digital Marketing', category: 'MARKETING', level: 'BEGINNER', icon: Globe, desc: 'Assess your knowledge of SEO, social media, ads, and online marketing strategies.' },
    { id: 19, title: 'UI/UX Design', category: 'DESIGN', level: 'INTERMEDIATE', icon: PenTool, desc: 'Test your ability to design user-friendly interfaces and improve user experience.' },
    { id: 20, title: 'App Development', category: 'DEVELOPMENT', level: 'ADVANCED', icon: Monitor, desc: 'Evaluate your skills in building mobile applications and app architecture.' },
    { id: 21, title: 'Algorithms', category: 'COMPUTER SCIENCE', level: 'INTERMEDIATE', icon: Code2, desc: 'Assess your problem-solving ability using efficient algorithmic approaches.' },
    { id: 22, title: 'Data Structures', category: 'COMPUTER SCIENCE', level: 'ADVANCED', icon: Layers, desc: 'Test your understanding of arrays, trees, graphs, and data organization techniques.' },
    { id: 23, title: 'SQL', category: 'DATABASES', level: 'INTERMEDIATE', icon: Database, desc: 'Evaluate your skills in writing queries, managing databases, and data retrieval.' },
    { id: 24, title: 'Databases', category: 'DATABASES', level: 'ADVANCED', icon: Database, desc: 'Test your knowledge of database design, normalization, and data management systems.' },
    { id: 25, title: 'Linux Shell', category: 'IT', level: 'INTERMEDIATE', icon: Terminal, desc: 'Assess your command-line skills and system operations using Linux environments.' },
    { id: 26, title: 'Biotechnology Fundamentals', category: 'SCIENCE', level: 'BEGINNER', icon: Dna, desc: 'Learn core biotechnology concepts including cells, DNA, enzymes, and lab techniques.' },
    { id: 27, title: 'Bioinformatics Basics', category: 'DATA SCIENCE', level: 'INTERMEDIATE', icon: Database, desc: 'Understand how computational tools are used to analyze biological data.' },
    { id: 28, title: 'AutoCAD Design', category: 'DESIGN', level: 'INTERMEDIATE', icon: PenTool, desc: 'Master your design skills using AutoCAD for creating precise 2D drawings.' }
  ];

  const handleStartCourse = (courseTitle) => {
    // Navigate to quiz page with course name as parameter
    navigate(`/quiz/${encodeURIComponent(courseTitle)}`);
  };

  const filteredCourses = courses.filter(course =>
    course.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div style={{marginTop:"40px"}} className="min-h-screen bg-[#f8fafc] pb-20">
      {/* Hero Section (Image 2) */}
      <section className="pt-20 pb-12 text-center px-4">
        <h1 className="text-5xl font-serif mb-6">
          Validate Your Skills. <span className="text-blue-500">Get Certified.</span>
        </h1>
        <p className="text-slate-500 text-lg mb-10 max-w-2xl mx-auto">
          Take industry-level skill assessments and earn verified certificates to showcase your expertise.
        </p>

        {/* Search Bar (Image 2) */}
        <div className="max-w-4xl mx-auto relative group">
          <div className="absolute inset-y-0 left-5 flex items-center pointer-events-none">
            <Search className="text-slate-400 group-focus-within:text-blue-500 transition-colors" size={20} />
          </div>
          <input
            type="text"
            placeholder="Search for a skill or domain..."
            className="w-full pl-14 pr-6 py-5 bg-white border border-slate-200 rounded-2xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-slate-700 text-lg"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </section>

      {/* Info Bar (Image 9) */}
      <div className="max-w-7xl mx-auto px-6 mb-12">
        <div className="bg-[#f1f5f9]/50 py-3 px-6 rounded-lg flex items-center justify-center gap-3 border border-slate-100">
          <Award className="text-blue-400" size={18} />
          <p className="text-slate-600 text-sm font-medium">Get a verified certificate after passing the assessment.</p>
        </div>
      </div>

      {/* Course Grid (Images 3-8) */}
      <main className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {filteredCourses.length > 0 ? (
            filteredCourses.map((course) => (
              <div 
                key={course.id} 
                className="bg-white rounded-[2rem] p-8 border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col group relative overflow-hidden cursor-pointer"
                onClick={() => handleStartCourse(course.title)}
              >
                {/* Level Tag */}
                <div className="absolute top-6 right-8">
                  <span className={`text-[10px] font-bold tracking-widest uppercase ${
                    course.level === 'BEGINNER' ? 'text-emerald-500' : 
                    course.level === 'INTERMEDIATE' ? 'text-blue-500' : 'text-purple-500'
                  }`}>
                    {course.level}
                  </span>
                </div>

                {/* Category Icon */}
                <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-300">
                  <course.icon className="text-blue-500" size={24} />
                </div>

                {/* Category Name */}
                <p className="text-[10px] font-bold text-blue-500 tracking-widest uppercase mb-3">
                  {course.category}
                </p>

                {/* Title */}
                <h3 className="text-2xl font-serif text-[#1e293b] mb-4">
                  {course.title}
                </h3>

                {/* Description */}
                <p className="text-slate-500 text-sm leading-relaxed mb-8 flex-1">
                  {course.desc}
                </p>

                {/* Meta Data */}
                <div className="grid grid-cols-2 gap-y-4 mb-8">
                  <div className="flex items-center gap-2 text-slate-400">
                    <Clock size={16} />
                    <span className="text-xs font-medium">12 minute</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-400">
                    <FileQuestion size={16} />
                    <span className="text-xs font-medium">10 Questions</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-400">
                    <ShieldCheck size={16} />
                    <span className="text-xs font-medium">Pass: &gt; 60%</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-400">
                    <Award size={16} />
                    <span className="text-xs font-medium">Certificate</span>
                  </div>
                </div>

                {/* Button */}
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStartCourse(course.title);
                  }}
                  className="text-blue-500 font-bold flex items-center gap-2 hover:gap-4 transition-all duration-300 group-hover:translate-x-1"
                >
                  Start Course <ChevronRight size={18} />
                </button>
              </div>
            ))
          ) : (
            <div className="col-span-full py-20 text-center">
              <p className="text-slate-400 text-lg">No courses found matching your search.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Skillcourse;